/* Direct simple-product ordering; exact line edits reuse the cart drawer queue. */
(() => {
  'use strict';
  if (window.NiralaMenuCart) {
    window.NiralaMenuCart.init(document);
    return;
  }

  const selector = '.np-product-card[data-product-id]';
  let quantities = null;
  let cartItems = null;
  let busy = false;
  let activeControl = null;
  let activeRegion = null;
  const buttonStates = new Map();
  const readOnlyStates = new Map();
  const tell = message => window.Nirala?.tell?.(message);
  const queueBusy = () => Boolean(window.NiralaCartControls?.isBusy?.());
  const lineProductId = item => String(item.product_id ?? item.product?.id ?? '');
  const quantityMaximum = value => {
    if (value === undefined || value === null || String(value).trim() === '') return 99;
    const maximum = Number(value);
    return Number.isFinite(maximum) ? Math.max(0, Math.min(99, maximum)) : 99;
  };

  function editableLine(card) {
    if (card.dataset.menuSimple !== 'true' || Number(card.dataset.menuMin || 1) !== 1 || Number(card.dataset.menuStep || 1) !== 1) return null;
    if (cartItems === null) {
      const controls = card.querySelector('[data-menu-line-controls]');
      if (controls?.dataset.menuLineReady !== 'true' || !controls.dataset.menuLineKey) return null;
      const quantity = Number(controls.querySelector('[data-menu-line-count]')?.textContent);
      return quantity > 0 ? { key: controls.dataset.menuLineKey, quantity } : null;
    }
    const matches = cartItems.filter(item => lineProductId(item) === card.dataset.productId);
    if (matches.length !== 1) return null;
    const item = matches[0];
    const customized = Object.entries(item.properties || {}).some(([name, value]) => name.startsWith('_') || String(value ?? '').trim() !== '');
    if (!item.key || Number(item.quantity) < 1 || item.selling_plan_allocation || item.selling_plan || item.parent_relationship || customized) return null;
    if (card.dataset.variantId && String(item.variant_id ?? item.id) !== card.dataset.variantId) return null;
    return item;
  }

  function lockFields() {
    if (!busy) return;
    document.querySelectorAll('[data-menu-add], [data-menu-line-change]').forEach(button => {
      if (!buttonStates.has(button)) buttonStates.set(button, button.disabled);
      window.NiralaBusyControls.set(button, true, buttonStates.get(button), button === activeControl);
    });
    document.querySelectorAll('[data-cart-drawer] [data-cart-quantity]').forEach(input => {
      if (!readOnlyStates.has(input)) readOnlyStates.set(input, input.readOnly);
      input.readOnly = true;
    });
  }

  function begin(control) {
    if (busy || queueBusy()) { tell('Please wait while your order is being updated.'); return false; }
    busy = true;
    activeControl = control;
    activeRegion = control?.closest('[data-menu-quick-add], [data-menu-line-controls]');
    activeRegion?.setAttribute('aria-busy', 'true');
    lockFields();
    return true;
  }

  function finish() {
    busy = false;
    buttonStates.forEach((disabled, button) => { window.NiralaBusyControls.set(button, false, disabled, false); });
    readOnlyStates.forEach((readOnly, input) => { input.readOnly = readOnly; });
    buttonStates.clear();
    readOnlyStates.clear();
    activeRegion?.removeAttribute('aria-busy');
    activeRegion = null;
    activeControl = null;
    init(document);
    document.dispatchEvent(new CustomEvent('nirala:menu-cart-idle'));
  }

  function cardsWithin(root) {
    const cards = [...root.querySelectorAll(selector)];
    if (root.matches?.(selector)) cards.unshift(root);
    return cards;
  }

  function renderCard(card) {
    const quantity = quantities === null ? Number(card.dataset.productCartQuantity || 0) : quantities.get(card.dataset.productId) || 0;
    card.dataset.productCartQuantity = String(quantity);
    const controls = card.querySelector('[data-menu-line-controls]');
    const line = editableLine(card);
    const editable = Boolean(controls && line && typeof window.NiralaCartControls?.apply === 'function');
    if (controls) {
      controls.hidden = !editable;
      if (editable) {
        controls.dataset.menuLineKey = line.key;
        const lineCount = controls.querySelector('[data-menu-line-count]');
        if (lineCount) lineCount.textContent = String(line.quantity);
        const maximum = quantityMaximum(card.dataset.menuMax);
        controls.querySelectorAll('[data-menu-line-change]').forEach(button => {
          const unavailable = Number(button.dataset.menuLineChange) > 0 && Number(line.quantity) >= maximum;
          button.disabled = unavailable;
          if (busy && buttonStates.has(button)) buttonStates.set(button, unavailable);
        });
      }
    }
    const addWrap = card.querySelector('[data-menu-add-wrap]');
    if (addWrap) addWrap.hidden = editable;
  }

  function init(root = document) {
    // Before the first Ajax cart event, retain the real server-rendered count.
    cardsWithin(root).forEach(renderCard);
    lockFields();
  }

  function update(cart) {
    if (!Array.isArray(cart?.items)) return false;
    const next = new Map();
    for (const item of cart.items) {
      const productId = item.product_id ?? item.product?.id;
      const quantity = Number(item.quantity);
      if (productId === undefined || productId === null || !Number.isInteger(quantity) || quantity < 0) return false;
      const id = String(productId);
      next.set(id, (next.get(id) || 0) + quantity);
    }
    quantities = next;
    cartItems = cart.items;
    init(document);
    return true;
  }

  async function add(form) {
    if (!begin(form.querySelector('[data-menu-add]'))) return false;
    try {
      const result = await window.Nirala.addToCart(form, { openCart: false, stayOnPage: true });
      if (result === false || result?.cancelled) return false;
      if (result?.error) throw new Error(result.message || String(result.error));
      if (!result?.cartRefreshFailed) tell(form.dataset.addedMessage || 'Added to your order.');
      return true;
    } catch (error) {
      tell(error.message || 'We could not confirm the addition. Please check your cart before trying again.');
      return false;
    } finally { finish(); }
  }

  async function change(card, direction) {
    if (typeof window.Nirala?.refreshCart !== 'function' || typeof window.NiralaCartControls?.apply !== 'function') return false;
    if (direction > 0 && window.NiralaStoreHours?.check && !window.NiralaStoreHours.check()) return false;
    if (!begin(card.querySelector(`[data-menu-line-change="${direction > 0 ? '1' : '-1'}"]`))) return false;
    try {
      const cart = await window.Nirala.refreshCart({ fresh: true });
      if (!update(cart)) throw new Error('Your cart could not be checked. Open your cart to review your order.');
      if (queueBusy()) throw new Error('Please wait while your order is being updated.');
      if (direction > 0 && window.NiralaStoreHours?.check && !window.NiralaStoreHours.check()) return false;
      const line = editableLine(card);
      if (!line) throw new Error('This product has different options or instructions. Open your cart to choose the exact item.');
      const form = document.querySelector('[data-cart-drawer] [data-cart-controls]');
      const input = [...(form?.querySelectorAll('[data-cart-quantity]') || [])].find(node => node.dataset.cartKey === line.key);
      if (!input) throw new Error('Open your cart to review this item before changing its quantity.');
      const quantity = Math.max(0, Number(line.quantity) + direction);
      const maximum = Math.min(quantityMaximum(input.max), quantityMaximum(card.dataset.menuMax));
      if (quantity > maximum) throw new Error(`The maximum quantity for this item is ${maximum}.`);
      input.value = String(quantity);
      const success = await window.NiralaCartControls.apply(form);
      if (!success) {
        const error = document.querySelector('[data-cart-drawer] [data-cart-error]:not([hidden])');
        tell(error?.textContent || 'Please open your cart to review the update.');
      }
      return Boolean(success);
    } catch (error) {
      tell(error.message || 'Your cart could not be updated. Please open Edit cart to review it.');
      return false;
    } finally { finish(); }
  }

  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-menu-quick-add]');
    if (form && typeof window.Nirala?.addToCart === 'function') {
      event.preventDefault();
      event.stopImmediatePropagation();
      add(form);
      return;
    }
    if (busy && event.target.matches('[data-cart-form], [data-quick-order-form]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      tell('Please wait while your order is being updated.');
    }
  }, true);

  document.addEventListener('click', event => {
    if (busy && event.target.closest('[data-cart-step], [data-cart-remove], [data-quick-order-open], [data-menu-line-change]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      tell('Please wait while your order is being updated.');
      return;
    }
    const button = event.target.closest('[data-menu-line-change]');
    const card = event.target.closest(selector);
    if (button && card && !button.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      change(card, Number(button.dataset.menuLineChange) > 0 ? 1 : -1);
      return;
    }
    // A tap on card whitespace opens options. Buttons, links and favourites
    // retain their own actions and never bubble into another add or popup.
    if (!card || !card.hasAttribute('data-menu-card') || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || event.target.closest('a, button, input, select, textarea, label, form, summary, [data-menu-line-controls]')) return;
    if (busy) { event.preventDefault(); return; }
    const link = card.querySelector('[data-quick-order-open]');
    if (link && typeof window.NiralaQuickOrder?.open === 'function') {
      event.preventDefault();
      window.NiralaQuickOrder.open(link);
    }
  }, true);

  document.addEventListener('nirala:cart-updated', event => update(event.detail));
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('pageshow', () => init(document));
  window.NiralaMenuCart = { init, update, add, change, isBusy: () => busy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
